import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, Button, Badge } from "@/components/design-system";

/* --------------------------------------------------------------------------
   Shared visual primitives
   -------------------------------------------------------------------------- */

const cn = (...xs) => xs.filter(Boolean).join(" ");

function SectionHeader({ eyebrow, title, description }) {
  return (
    <header className="mb-8">
      {eyebrow && (
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">{eyebrow}</p>
      )}
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl mt-2 text-[#0B1F3A]">{title}</h1>
      {description && (
        <p className="mt-3 text-[#0B1F3A]/70 max-w-2xl text-base">{description}</p>
      )}
    </header>
  );
}

function StepPill({ n, tone = "blue" }) {
  const tones = {
    blue: "bg-[#2D7FF9] text-white",
    green: "bg-[#22C55E] text-white",
    navy: "bg-[#0B1F3A] text-white",
    cyan: "bg-[#20C7E8] text-white",
    gold: "bg-[#F59E0B] text-white",
  };
  return (
    <span className={cn("inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-extrabold shadow-sm", tones[tone])}>
      {n}
    </span>
  );
}

function InfoStep({ n, tone, title, children, tag }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <StepPill n={n} tone={tone} />
        <div className="flex-1 w-px bg-[#0B1F3A]/10 mt-2" />
      </div>
      <div className="pb-8 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">{title}</h3>
          {tag && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#2D7FF9] bg-[#E8F1FF] px-2 py-0.5 rounded-full">{tag}</span>
          )}
        </div>
        <div className="mt-2 text-sm text-[#0B1F3A]/75 leading-relaxed space-y-2">
          {children}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Founder journey diagram (SVG infographic)
   -------------------------------------------------------------------------- */

function FounderLoopDiagram() {
  const steps = [
    { label: "Validate", tone: "#2D7FF9" },
    { label: "Improve", tone: "#20C7E8" },
    { label: "Launch",  tone: "#0B1F3A" },
    { label: "Promote", tone: "#22C55E" },
    { label: "Grow",    tone: "#F59E0B" },
  ];

  return (
    <div className="relative rounded-2xl bg-gradient-to-br from-[#0B1F3A] to-[#12305A] text-white p-6 md:p-8 overflow-hidden" data-testid="hdiw-founder-loop">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#2D7FF9]/20 blur-3xl" aria-hidden />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#22C55E]/15 blur-3xl" aria-hidden />
      <div className="relative">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">The launch loop</p>
        <h2 className="font-[Montserrat] font-extrabold text-2xl md:text-3xl mt-1">One continuous workflow</h2>
        <p className="mt-2 text-white/70 text-sm max-w-xl">Every product on SaaSLaunchingLab moves through the same five-stage cycle — powered by real users and AI insights.</p>

        <div className="mt-6 grid grid-cols-5 gap-2 md:gap-3">
          {steps.map((s, i) => (
            <div key={s.label} className="flex flex-col items-center text-center">
              <div
                className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center font-extrabold text-lg shadow-lg ring-4 ring-white/10"
                style={{ background: s.tone }}
                aria-hidden
              >{i + 1}</div>
              <div className="mt-2 text-xs md:text-sm font-bold uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Connecting line + arrows */}
        <div className="mt-4 relative h-6" aria-hidden>
          <div className="absolute left-[10%] right-[10%] top-1/2 h-[3px] rounded-full bg-gradient-to-r from-[#2D7FF9] via-[#20C7E8] via-[#22C55E] to-[#F59E0B]" />
        </div>

        <div className="mt-4 grid grid-cols-5 gap-2 md:gap-3 text-[11px] text-white/70 leading-tight">
          <div>Get structured feedback from qualified experts.</div>
          <div>Ship AI-summarised changes to your product.</div>
          <div>Publish your public product page.</div>
          <div>Recruit verified ambassadors on revenue share.</div>
          <div>Convert paying customers over 12 months.</div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Community journey diagram (SVG infographic)
   -------------------------------------------------------------------------- */

function CommunityLadderDiagram() {
  const tiers = [
    { label: "Explorer",   range: "0 – 24 pts",  tone: "#94A3B8", desc: "You've just joined. Browse products & submit your first feedback." },
    { label: "Validator",  range: "25 – 99 pts", tone: "#2D7FF9", desc: "You give quality feedback. Unlock more campaigns and premium perks." },
    { label: "Ambassador", range: "100+ pts",    tone: "#22C55E", desc: "Apply to become a verified ambassador and earn revenue share." },
  ];

  return (
    <div className="relative rounded-2xl bg-gradient-to-br from-[#0B1F3A] to-[#12305A] text-white p-6 md:p-8 overflow-hidden" data-testid="hdiw-community-ladder">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#22C55E]/15 blur-3xl" aria-hidden />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#2D7FF9]/20 blur-3xl" aria-hidden />

      <div className="relative">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">Reputation ladder</p>
        <h2 className="font-[Montserrat] font-extrabold text-2xl md:text-3xl mt-1">Grow your reputation, earn your role</h2>
        <p className="mt-2 text-white/70 text-sm max-w-xl">The more quality feedback you give, the more you unlock — from access to premium campaigns to a paid ambassador seat.</p>

        <div className="mt-6 space-y-3">
          {tiers.map((t, i) => (
            <div key={t.label} className="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center font-extrabold text-sm shadow-lg"
                style={{ background: t.tone }}
                aria-hidden
              >{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <div className="font-[Montserrat] font-bold text-lg">{t.label}</div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-white/60">{t.range}</div>
                </div>
                <div className="mt-1 text-sm text-white/70">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl bg-white/10 border border-white/15 p-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-[11px] font-bold uppercase tracking-widest text-white/60">Bonus</span>
          <span className="text-white/85">Referred a founder who becomes paying? Earn <b className="text-[#22C55E]">12.5% recurring</b> for up to 12 months.</span>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Small infographic: reputation points breakdown (community)
   -------------------------------------------------------------------------- */

function PointsBreakdown() {
  const rows = [
    { label: "Submit structured feedback",     points: "+10", color: "#2D7FF9" },
    { label: "Feedback marked helpful by founder", points: "+5",  color: "#20C7E8" },
    { label: "Complete a full campaign",       points: "+15", color: "#22C55E" },
    { label: "Refer a founder who signs up",   points: "+25", color: "#F59E0B" },
  ];
  return (
    <Card className="p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Points cheatsheet</p>
      <h3 className="font-[Montserrat] font-bold text-lg mt-1 text-[#0B1F3A]">How do you earn reputation?</h3>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3">
            <div className="w-1.5 h-8 rounded-full" style={{ background: r.color }} aria-hidden />
            <div className="flex-1 text-sm text-[#0B1F3A]">{r.label}</div>
            <div className="text-sm font-extrabold" style={{ color: r.color }}>{r.points}</div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-[#0B1F3A]/55">Rules are transparent and stored in an append-only event log — you can always inspect your history under Reputation.</p>
    </Card>
  );
}

/* --------------------------------------------------------------------------
   Small infographic: revenue split (founder)
   -------------------------------------------------------------------------- */

function RevenueSplitDiagram() {
  return (
    <Card className="p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Ambassador economics</p>
      <h3 className="font-[Montserrat] font-bold text-lg mt-1 text-[#0B1F3A]">How referral revenue is split</h3>
      <p className="mt-2 text-sm text-[#0B1F3A]/70">When a verified ambassador brings you a paying customer, revenue is split automatically over 12 months.</p>

      <div className="mt-5 space-y-4">
        <div>
          <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-[#0B1F3A]/70">
            <span>You (Founder)</span><span>85%</span>
          </div>
          <div className="mt-1 h-3 rounded-full bg-[#E8F1FF] overflow-hidden">
            <div className="h-full bg-[#2D7FF9]" style={{ width: "85%" }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-[#0B1F3A]/70">
            <span>Ambassador</span><span>12.5%</span>
          </div>
          <div className="mt-1 h-3 rounded-full bg-[#E8F1FF] overflow-hidden">
            <div className="h-full bg-[#22C55E]" style={{ width: "12.5%" }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-[#0B1F3A]/70">
            <span>Platform</span><span>2.5%</span>
          </div>
          <div className="mt-1 h-3 rounded-full bg-[#E8F1FF] overflow-hidden">
            <div className="h-full bg-[#0B1F3A]" style={{ width: "2.5%" }} />
          </div>
        </div>
      </div>
      <p className="mt-4 text-[11px] text-[#0B1F3A]/55">No referral? You keep <b>97.5%</b>, platform keeps 2.5%. Attribution window is exactly 12 months from the referred signup.</p>
    </Card>
  );
}

/* --------------------------------------------------------------------------
   FOUNDER content
   -------------------------------------------------------------------------- */

function FounderView() {
  return (
    <div className="space-y-8" data-testid="hdiw-founder">
      <SectionHeader
        eyebrow="How does it work?"
        title="Ship your SaaS with real users, not guesswork."
        description="You bring the product. We bring the qualified experts, the AI-powered insights, and a verified ambassador network that only gets paid when your customers do."
      />

      <FounderLoopDiagram />

      <Card className="p-6 md:p-8">
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Your step-by-step playbook</h2>
        <p className="mt-1 text-sm text-[#0B1F3A]/70">A guided path from your first product page to your first paying, referred customer.</p>

        <div className="mt-6">
          <InfoStep n="1" tone="blue" title="Add your product" tag="~2 minutes">
            <p>Create a product with a name, one-liner, logo and link. It becomes your public page at <code className="text-[#2D7FF9]">/p/your-slug</code> — instantly SEO-ready and shareable.</p>
            <div><Link to="/app/products" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to My products</Link></div>
          </InfoStep>

          <InfoStep n="2" tone="cyan" title="Launch a validation campaign" tag="Structured feedback">
            <p>Pick one of three snapshot templates (concept, MVP or growth) and open a campaign. Community experts submit structured feedback across pre-defined axes — pricing, positioning, UX, category fit.</p>
            <div><Link to="/app/campaigns" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Campaigns</Link></div>
          </InfoStep>

          <InfoStep n="3" tone="navy" title="Read your AI insight report" tag="GPT-5.5">
            <p>Once the campaign closes, our AI merges all responses into a single actionable report: top strengths, top risks, recommended fixes, and a validation score you can quote to investors.</p>
            <div><Link to="/app/reports" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Feedback reports</Link></div>
          </InfoStep>

          <InfoStep n="4" tone="green" title="Recruit verified ambassadors" tag="Revenue share">
            <p>Browse the ambassador directory and invite verified members to promote your product. Every ambassador gets a unique referral link and is paid 12.5% of any revenue they bring in for 12 months — automatically.</p>
            <div><Link to="/app/ambassadors" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Find ambassadors</Link></div>
          </InfoStep>

          <InfoStep n="5" tone="gold" title="Grow with recurring payouts" tag="12-month window">
            <p>Track referred signups, MRR generated, and monthly payouts under Payouts. You only pay ambassadors when you get paid — zero risk, aligned incentives.</p>
            <div><Link to="/app/payouts" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Payouts</Link></div>
          </InfoStep>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <RevenueSplitDiagram />
        <Card className="p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Frequently asked</p>
          <h3 className="font-[Montserrat] font-bold text-lg mt-1 text-[#0B1F3A]">Founder FAQ</h3>
          <dl className="mt-4 space-y-4 text-sm text-[#0B1F3A]/80">
            <div>
              <dt className="font-bold text-[#0B1F3A]">Do I need to pay upfront?</dt>
              <dd className="mt-1">No. You pay a small monthly or yearly plan for validation & AI reports. Ambassador payouts only trigger on real, paying customers.</dd>
            </div>
            <div>
              <dt className="font-bold text-[#0B1F3A]">Who are the reviewers?</dt>
              <dd className="mt-1">Verified community members with a public reputation score. You can see their tier (Explorer / Validator / Ambassador) before they join your campaign.</dd>
            </div>
            <div>
              <dt className="font-bold text-[#0B1F3A]">How long does a campaign take?</dt>
              <dd className="mt-1">Typically 5–10 days depending on target audience size. Campaigns auto-close and generate the AI report automatically.</dd>
            </div>
          </dl>
        </Card>
      </div>

      <Card className="p-6 md:p-8 bg-gradient-to-br from-[#E8F1FF] to-white border-[#2D7FF9]/20">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div>
            <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Ready to launch your first campaign?</h3>
            <p className="mt-1 text-sm text-[#0B1F3A]/70">Add your product and open a validation campaign in under 5 minutes.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/app/products/new"><Button variant="primary" data-testid="hdiw-founder-cta-product">Add a product</Button></Link>
            <Link to="/app/campaigns"><Button variant="secondary" data-testid="hdiw-founder-cta-campaign">Open a campaign</Button></Link>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------------------
   COMMUNITY content
   -------------------------------------------------------------------------- */

function CommunityView() {
  return (
    <div className="space-y-8" data-testid="hdiw-community">
      <SectionHeader
        eyebrow="How does it work?"
        title="Give feedback. Grow reputation. Earn recurring revenue."
        description="SaaSLaunchingLab turns your product intuition into a track record — and pays you when the founders you back become successful."
      />

      <CommunityLadderDiagram />

      <Card className="p-6 md:p-8">
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Your path from Explorer to Ambassador</h2>
        <p className="mt-1 text-sm text-[#0B1F3A]/70">Four simple steps. No prior expertise required — just curiosity and honesty.</p>

        <div className="mt-6">
          <InfoStep n="1" tone="blue" title="Explore live products" tag="Free">
            <p>Browse founder submissions in Explore. Filter by category, stage, or ambassador-eligibility. Each product has a public page with pitch, screenshots, and open campaigns.</p>
            <div><Link to="/app/explore" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Explore</Link></div>
          </InfoStep>

          <InfoStep n="2" tone="cyan" title="Submit structured feedback" tag="+10 pts each">
            <p>Join a campaign and answer a set of guided questions — no walls of text needed. Your feedback becomes part of the founder&apos;s AI report and earns you reputation points.</p>
            <div><Link to="/app/my-feedback" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to My feedback</Link></div>
          </InfoStep>

          <InfoStep n="3" tone="green" title="Climb the reputation ladder" tag="25 → 100 pts">
            <p>Reach 25 points to unlock the <b>Validator</b> tier, and 100+ to become <b>Ambassador-eligible</b>. Your points, tier and history are always transparent under Reputation.</p>
            <div><Link to="/app/reputation" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Reputation</Link></div>
          </InfoStep>

          <InfoStep n="4" tone="gold" title="Apply to become a verified Ambassador" tag="Paid role">
            <p>Once eligible, apply from the Ambassador page. Our AI scores your application and admins verify it. Approved ambassadors get a public profile at <code className="text-[#2D7FF9]">/a/your-slug</code> and unique referral links per product.</p>
            <div><Link to="/app/ambassadors" className="text-[#2D7FF9] font-semibold hover:underline">→ Go to Ambassador</Link></div>
          </InfoStep>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <PointsBreakdown />

        <Card className="p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#22C55E]">Ambassador rewards</p>
          <h3 className="font-[Montserrat] font-bold text-lg mt-1 text-[#0B1F3A]">What you earn once verified</h3>
          <div className="mt-4 space-y-3 text-sm text-[#0B1F3A]/80">
            <div className="flex gap-3">
              <div className="w-1.5 h-8 rounded-full bg-[#22C55E]" aria-hidden />
              <div><b className="text-[#0B1F3A]">12.5% recurring revenue share</b> on every paying customer you refer, for 12 months.</div>
            </div>
            <div className="flex gap-3">
              <div className="w-1.5 h-8 rounded-full bg-[#2D7FF9]" aria-hidden />
              <div><b className="text-[#0B1F3A]">Public SEO profile</b> at <code className="text-[#2D7FF9]">/a/your-slug</code> — great for your personal brand.</div>
            </div>
            <div className="flex gap-3">
              <div className="w-1.5 h-8 rounded-full bg-[#20C7E8]" aria-hidden />
              <div><b className="text-[#0B1F3A]">Priority access</b> to founder invitations and premium campaigns.</div>
            </div>
            <div className="flex gap-3">
              <div className="w-1.5 h-8 rounded-full bg-[#F59E0B]" aria-hidden />
              <div><b className="text-[#0B1F3A]">Monthly automated payouts</b> — track everything under Earnings.</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6 md:p-8 bg-gradient-to-br from-[#E8F1FF] to-white border-[#2D7FF9]/20">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div>
            <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Ready to give your first feedback?</h3>
            <p className="mt-1 text-sm text-[#0B1F3A]/70">Open Explore, pick a product you like, and submit a campaign response — you&apos;ll get your first points immediately.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/app/explore"><Button variant="primary" data-testid="hdiw-community-cta-explore">Explore products</Button></Link>
            <Link to="/app/reputation"><Button variant="secondary" data-testid="hdiw-community-cta-rep">See my reputation</Button></Link>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Role picker (admins/ambassadors get to choose which view to read)
   -------------------------------------------------------------------------- */

function RolePicker({ value, onChange }) {
  const options = [
    { id: "founder",   label: "I'm a Founder" },
    { id: "community", label: "I'm a Community member" },
  ];
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-[#0B1F3A]/10 p-1 bg-white mb-6" data-testid="hdiw-role-picker">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          data-testid={`hdiw-picker-${o.id}`}
          className={cn(
            "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors",
            value === o.id ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70 hover:text-[#0B1F3A]"
          )}
        >{o.label}</button>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Entry
   -------------------------------------------------------------------------- */

export default function HowDoesItWork() {
  const { user } = useAuth();
  const active = user?.active_role || user?.roles?.[0] || "community";

  // For roles that aren't founder/community, default to community view but allow toggle.
  const [pick, setPick] = React.useState(
    active === "founder" ? "founder" : active === "community" ? "community" : "community"
  );

  const canToggle = !(active === "founder" || active === "community")
    || (user?.roles?.includes("founder") && user?.roles?.includes("community"));

  return (
    <div data-testid="how-does-it-work-page">
      {canToggle && <RolePicker value={pick} onChange={setPick} />}
      {(canToggle ? pick : active) === "founder" ? <FounderView /> : <CommunityView />}
    </div>
  );
}
